// =====================================================
// AUDIT EXPORT SERVICE - PDF Generation
// Institution-grade compliance reporting
// =====================================================

import { jsPDF } from 'jspdf';
import { supabase } from '@/integrations/supabase/client';
import { getJournalStats, getJournalEntries } from './journalService';
import { getCounterfactualStats, getCounterfactualForEntry } from './counterfactualService';
import { getLatestTDIScore } from './disciplineScoreService';
import type { 
  AuditReport, 
  AuditReportParams,
  AuditSummary,
  AuditDisciplineMetrics,
  AuditCounterfactualSummary,
  AuditCounterfactualEntry,
  AuditOutcomeDistribution,
  AuditByCategory,
  AuditDecisionLogEntry,
  DateRangePreset
} from '@/types/auditReport';
import { getDateRangeFromPreset, getDisciplineInterpretation } from '@/types/auditReport';
import { getTierFromScore } from '@/types/disciplineScore';
import type { JournalEntry } from '@/types/journal';

// =====================================================
// DATA FETCHING
// =====================================================

async function fetchAuditData(params: AuditReportParams): Promise<AuditReport | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('[AuditExport] No authenticated user');
      return null;
    }

    // Calculate days for stats query
    const daysDiff = Math.ceil(
      (params.dateRange.to.getTime() - params.dateRange.from.getTime()) / (1000 * 60 * 60 * 24)
    );
    const days = daysDiff > 365 ? undefined : daysDiff;

    // Fetch all data in parallel
    const [stats, cfStats, tdiScore, entries] = await Promise.all([
      getJournalStats(days),
      getCounterfactualStats(days),
      getLatestTDIScore(),
      getJournalEntries({
        startDate: params.dateRange.from.toISOString(),
        endDate: params.dateRange.to.toISOString(),
        marketType: params.filters?.marketType,
        assetSymbol: params.filters?.assetSymbol,
      }),
    ]);

    if (!stats) {
      console.error('[AuditExport] Failed to fetch stats');
      return null;
    }

    // Build summary
    const winRate = stats.wins + stats.losses > 0 
      ? (stats.wins / (stats.wins + stats.losses)) * 100 
      : null;

    const summary: AuditSummary = {
      userName: user.email?.split('@')[0] || 'Trader',
      userId: user.id.slice(0, 8),
      generatedAt: new Date(),
      dateRange: {
        from: params.dateRange.from,
        to: params.dateRange.to,
        label: `${params.dateRange.from.toLocaleDateString()} - ${params.dateRange.to.toLocaleDateString()}`,
      },
      tdiScore: tdiScore?.tdiScore || null,
      tdiTier: tdiScore?.tier || 'N/A',
      decisionQualityScore: stats.disciplineScore,
      capitalPreservationRate: stats.capitalPreservationRate,
      riskAvoidanceScore: stats.riskAvoidanceScore,
      totalAnalyses: stats.totalAnalyses,
      tradeSignals: stats.tradeDecisions,
      noTradeDecisions: stats.noTradeDecisions,
      waitDecisions: stats.waitDecisions,
      wins: stats.wins,
      losses: stats.losses,
      breakevens: stats.breakevens,
      winRate,
    };

    // Build discipline metrics
    const disciplineMetrics: AuditDisciplineMetrics = {
      capitalPreservationScore: tdiScore?.components?.capital_preservation || stats.capitalPreservationRate,
      decisionConsistencyScore: tdiScore?.components?.decision_consistency || 50,
      riskAvoidanceScore: tdiScore?.components?.risk_avoidance || stats.riskAvoidanceScore,
      behavioralStabilityScore: tdiScore?.components?.behavioral_stability || 50,
      disciplineStatus: stats.disciplineDriftLevel as 'Stable' | 'Drift' | 'Overtrading',
      regimeCompliance: stats.regimeCompliance,
      interpretation: '',
    };
    disciplineMetrics.interpretation = getDisciplineInterpretation(disciplineMetrics);

    // Build counterfactual summary with notable examples
    const noTradeWaitEntries = entries.filter(
      e => e.executionPermission === 'NO_TRADE' || e.executionPermission === 'WAIT'
    );
    
    const notableExamples: AuditCounterfactualEntry[] = [];
    for (const entry of noTradeWaitEntries.slice(0, 5)) {
      const cf = await getCounterfactualForEntry(entry.id);
      if (cf) {
        notableExamples.push({
          asset: entry.assetSymbol,
          date: new Date(entry.createdAt).toLocaleDateString(),
          inputPrice: entry.inputPrice || 0,
          decision: entry.executionPermission,
          reason: entry.primaryReason,
          priceAfter1h: cf.priceAfter1h,
          priceAfter4h: cf.priceAfter4h,
          priceAfter24h: cf.priceAfter24h,
          capitalPreservedR: cf.capitalPreservedR,
          capitalPreservedUsd: cf.capitalPreservedUsd,
        });
      }
    }

    const counterfactual: AuditCounterfactualSummary = {
      totalNoTradeWaitDecisions: stats.noTradeDecisions + stats.waitDecisions,
      completedTracking: cfStats?.completedTracking || 0,
      totalCapitalPreservedR: cfStats?.totalCapitalPreservedR || 0,
      totalCapitalPreservedUsd: cfStats?.totalCapitalPreservedUsd || 0,
      decisionsThatSavedCapital: cfStats?.decisionsThatSavedCapital || 0,
      decisionsThatMissedOpportunity: cfStats?.decisionsThatMissedOpportunity || 0,
      notableExamples,
    };

    // Build outcome distribution
    const outcomeDistribution: AuditOutcomeDistribution = {
      wins: stats.wins,
      losses: stats.losses,
      breakevens: stats.breakevens,
      missed: stats.missed,
      cancelled: stats.cancelled,
      noTrade: stats.noTradeOutcomes,
      wait: stats.waitOutcomes,
      pending: stats.pending,
    };

    // Build category breakdown
    const categoryBreakdown: AuditByCategory = {
      byMarketType: stats.byMarket || {},
      byAsset: stats.byAsset || {},
      byRegime: stats.byRegime || {},
    };

    // Build decision log
    const decisionLog: AuditDecisionLogEntry[] = entries.map(entry => ({
      date: new Date(entry.createdAt).toLocaleDateString(),
      asset: entry.assetSymbol,
      decision: entry.executionPermission,
      direction: entry.direction,
      regime: entry.marketRegime,
      primaryReason: entry.primaryReason,
      outcome: entry.outcomeStatus,
      rMultiple: entry.outcomeRMultiple,
    }));

    return {
      summary,
      disciplineMetrics,
      counterfactual,
      outcomeDistribution,
      categoryBreakdown,
      decisionLog,
    };
  } catch (error) {
    console.error('[AuditExport] Error fetching audit data:', error);
    return null;
  }
}

// =====================================================
// PDF GENERATION - Professional Institutional Design
// =====================================================

function generatePDF(report: AuditReport): jsPDF {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Color palette (RGB)
  const colors = {
    primary: [147, 51, 234] as [number, number, number],      // Purple
    secondary: [34, 211, 238] as [number, number, number],    // Cyan
    success: [52, 211, 153] as [number, number, number],      // Emerald
    warning: [251, 191, 36] as [number, number, number],      // Amber
    danger: [239, 68, 68] as [number, number, number],        // Red
    dark: [17, 24, 39] as [number, number, number],           // Gray-900
    darkCard: [31, 41, 55] as [number, number, number],       // Gray-800
    text: [55, 65, 81] as [number, number, number],           // Gray-700
    muted: [107, 114, 128] as [number, number, number],       // Gray-500
    light: [243, 244, 246] as [number, number, number],       // Gray-100
    white: [255, 255, 255] as [number, number, number],
  };

  // Helper functions
  const addText = (text: string, x: number, y: number, options?: { 
    fontSize?: number; 
    fontStyle?: string; 
    color?: [number, number, number];
    align?: 'left' | 'center' | 'right';
  }) => {
    doc.setFontSize(options?.fontSize || 10);
    doc.setFont('helvetica', options?.fontStyle || 'normal');
    doc.setTextColor(...(options?.color || colors.text));
    
    let xPos = x;
    if (options?.align === 'center') {
      xPos = pageWidth / 2;
    } else if (options?.align === 'right') {
      xPos = pageWidth - margin;
    }
    doc.text(text, xPos, y, { align: options?.align || 'left' });
  };

  const drawCard = (x: number, y: number, w: number, h: number, bgColor: [number, number, number] = colors.light) => {
    doc.setFillColor(...bgColor);
    doc.roundedRect(x, y, w, h, 4, 4, 'F');
  };

  const drawLine = (y: number, color: [number, number, number] = colors.light) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
  };

  const checkNewPage = (neededSpace: number): boolean => {
    if (yPos + neededSpace > pageHeight - margin - 10) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // Get tier color
  const getTierColor = (score: number | null): [number, number, number] => {
    if (!score) return colors.muted;
    if (score >= 750) return colors.success;
    if (score >= 650) return colors.secondary;
    if (score >= 550) return colors.primary;
    return colors.muted;
  };

  // ==================== PAGE 1: EXECUTIVE SUMMARY ====================

  // Header bar
  drawCard(0, 0, pageWidth, 45, colors.dark);
  addText('SNAP CHART AI', margin, 18, { fontSize: 22, fontStyle: 'bold', color: colors.white });
  addText('Institutional Trading Audit Report', margin, 28, { fontSize: 11, color: colors.muted });
  addText(report.summary.dateRange.label, pageWidth - margin, 28, { fontSize: 10, color: colors.secondary, align: 'right' });
  
  yPos = 55;

  // Trader Info
  addText(`Trader: ${report.summary.userName}`, margin, yPos, { fontSize: 10, color: colors.text });
  addText(`Generated: ${report.summary.generatedAt.toLocaleDateString()}`, pageWidth - margin, yPos, { fontSize: 9, color: colors.muted, align: 'right' });
  yPos += 12;

  // TDI Score Card (Hero)
  const tdiCardHeight = 55;
  drawCard(margin, yPos, contentWidth, tdiCardHeight, colors.darkCard);
  
  const tdiColor = getTierColor(report.summary.tdiScore);
  
  // TDI Left side - Score
  addText('TRADER DISCIPLINE INDEX', margin + 12, yPos + 12, { fontSize: 9, fontStyle: 'bold', color: colors.muted });
  addText(report.summary.tdiScore?.toString() || '---', margin + 12, yPos + 35, { fontSize: 36, fontStyle: 'bold', color: tdiColor });
  addText(`/ 850`, margin + 60, yPos + 35, { fontSize: 12, color: colors.muted });
  
  // TDI Right side - Tier badge
  const tierBadgeX = pageWidth - margin - 50;
  doc.setFillColor(...tdiColor);
  doc.roundedRect(tierBadgeX, yPos + 10, 40, 18, 9, 9, 'F');
  addText(report.summary.tdiTier || 'N/A', tierBadgeX + 20, yPos + 22, { fontSize: 10, fontStyle: 'bold', color: colors.white, align: 'center' });
  
  yPos += tdiCardHeight + 10;

  // Key Metrics Row (3 cards)
  const metricCardWidth = (contentWidth - 10) / 3;
  const metricCardHeight = 40;
  
  const metrics = [
    { label: 'Decision Quality', value: `${report.summary.decisionQualityScore}`, unit: '/100', color: colors.primary },
    { label: 'Capital Preservation', value: `${report.summary.capitalPreservationRate.toFixed(1)}`, unit: '%', color: colors.success },
    { label: 'Risk Avoidance', value: `${report.summary.riskAvoidanceScore}`, unit: '/100', color: colors.secondary },
  ];

  metrics.forEach((metric, i) => {
    const x = margin + i * (metricCardWidth + 5);
    drawCard(x, yPos, metricCardWidth, metricCardHeight, colors.light);
    addText(metric.label, x + 8, yPos + 12, { fontSize: 8, color: colors.muted });
    addText(metric.value, x + 8, yPos + 28, { fontSize: 18, fontStyle: 'bold', color: metric.color });
    addText(metric.unit, x + 8 + doc.getTextWidth(metric.value) * 1.8 + 2, yPos + 28, { fontSize: 10, color: colors.muted });
  });

  yPos += metricCardHeight + 15;

  // Decision Breakdown Section
  drawLine(yPos, colors.light);
  yPos += 8;
  addText('DECISION BREAKDOWN', margin, yPos, { fontSize: 10, fontStyle: 'bold', color: colors.dark });
  yPos += 10;

  // Decision stats in grid
  const decisionStats = [
    { label: 'Total Analyses', value: report.summary.totalAnalyses.toString() },
    { label: 'TRADE Signals', value: report.summary.tradeSignals.toString() },
    { label: 'NO_TRADE', value: report.summary.noTradeDecisions.toString(), highlight: true },
    { label: 'WAIT', value: report.summary.waitDecisions.toString() },
    { label: 'Wins', value: report.summary.wins.toString(), color: colors.success },
    { label: 'Losses', value: report.summary.losses.toString(), color: colors.danger },
  ];

  const colWidth = contentWidth / 3;
  decisionStats.forEach((stat, i) => {
    const x = margin + (i % 3) * colWidth;
    const y = yPos + Math.floor(i / 3) * 16;
    addText(stat.label, x, y, { fontSize: 9, color: colors.muted });
    addText(stat.value, x + 50, y, { fontSize: 11, fontStyle: 'bold', color: stat.color || colors.text });
  });

  yPos += 38;

  // Discipline Components
  drawLine(yPos, colors.light);
  yPos += 8;
  addText('TDI COMPONENTS', margin, yPos, { fontSize: 10, fontStyle: 'bold', color: colors.dark });
  yPos += 12;

  const components = [
    { label: 'Capital Preservation', score: report.disciplineMetrics.capitalPreservationScore, weight: 35 },
    { label: 'Decision Consistency', score: report.disciplineMetrics.decisionConsistencyScore, weight: 30 },
    { label: 'Risk Avoidance', score: report.disciplineMetrics.riskAvoidanceScore, weight: 20 },
    { label: 'Behavioral Stability', score: report.disciplineMetrics.behavioralStabilityScore, weight: 15 },
  ];

  components.forEach((comp, i) => {
    const barWidth = 80;
    const x = margin;
    const y = yPos + i * 14;
    
    addText(comp.label, x, y, { fontSize: 9, color: colors.text });
    
    // Progress bar background
    doc.setFillColor(...colors.light);
    doc.roundedRect(x + 70, y - 4, barWidth, 6, 2, 2, 'F');
    
    // Progress bar fill
    const fillWidth = (comp.score / 100) * barWidth;
    doc.setFillColor(...colors.primary);
    doc.roundedRect(x + 70, y - 4, fillWidth, 6, 2, 2, 'F');
    
    addText(`${comp.score.toFixed(0)}`, x + 155, y, { fontSize: 9, fontStyle: 'bold', color: colors.text });
    addText(`(${comp.weight}%)`, x + 170, y, { fontSize: 8, color: colors.muted });
  });

  yPos += 62;

  // Status row
  const statusColor = report.disciplineMetrics.disciplineStatus === 'Stable' ? colors.success :
                      report.disciplineMetrics.disciplineStatus === 'Drift' ? colors.warning : colors.danger;
  
  drawCard(margin, yPos, contentWidth, 22, colors.light);
  addText('Discipline Status:', margin + 8, yPos + 14, { fontSize: 9, color: colors.muted });
  addText(report.disciplineMetrics.disciplineStatus.toUpperCase(), margin + 65, yPos + 14, { fontSize: 10, fontStyle: 'bold', color: statusColor });
  addText(`Regime Compliance: ${report.disciplineMetrics.regimeCompliance}%`, pageWidth - margin - 8, yPos + 14, { fontSize: 9, color: colors.text, align: 'right' });

  yPos += 30;

  // ==================== PAGE 2: COUNTERFACTUAL & DETAILS ====================

  checkNewPage(100);
  
  // Counterfactual Proof Section
  addText('COUNTERFACTUAL PROOF', margin, yPos, { fontSize: 10, fontStyle: 'bold', color: colors.dark });
  yPos += 4;
  addText('What happened after NO_TRADE/WAIT decisions', margin, yPos, { fontSize: 8, color: colors.muted });
  yPos += 10;

  const cfCardHeight = 45;
  drawCard(margin, yPos, contentWidth, cfCardHeight, colors.darkCard);
  
  const cfColor = report.counterfactual.totalCapitalPreservedR >= 0 ? colors.success : colors.warning;
  const cfSign = report.counterfactual.totalCapitalPreservedR >= 0 ? '+' : '';
  
  addText('Capital Preserved', margin + 10, yPos + 12, { fontSize: 9, color: colors.muted });
  addText(`${cfSign}${report.counterfactual.totalCapitalPreservedR.toFixed(1)}R`, margin + 10, yPos + 28, { fontSize: 24, fontStyle: 'bold', color: cfColor });
  addText(`($${Math.abs(report.counterfactual.totalCapitalPreservedUsd).toLocaleString()})`, margin + 55, yPos + 28, { fontSize: 10, color: colors.muted });
  
  addText(`${report.counterfactual.decisionsThatSavedCapital} decisions saved capital`, pageWidth - margin - 10, yPos + 16, { fontSize: 9, color: colors.success, align: 'right' });
  addText(`${report.counterfactual.decisionsThatMissedOpportunity} missed opportunities`, pageWidth - margin - 10, yPos + 28, { fontSize: 9, color: colors.warning, align: 'right' });

  yPos += cfCardHeight + 12;

  // Philosophy quote
  drawCard(margin, yPos, contentWidth, 20, [243, 232, 255]); // light purple
  addText('"Anyone can take a trade. Professionals know when not to."', margin + 8, yPos + 13, { fontSize: 9, fontStyle: 'italic', color: colors.primary });
  yPos += 28;

  // Outcome Distribution
  drawLine(yPos, colors.light);
  yPos += 8;
  addText('OUTCOME DISTRIBUTION', margin, yPos, { fontSize: 10, fontStyle: 'bold', color: colors.dark });
  yPos += 12;

  const outcomes = [
    { label: 'Wins', value: report.outcomeDistribution.wins, color: colors.success },
    { label: 'Losses', value: report.outcomeDistribution.losses, color: colors.danger },
    { label: 'Breakeven', value: report.outcomeDistribution.breakevens, color: colors.muted },
    { label: 'NO_TRADE', value: report.outcomeDistribution.noTrade, color: colors.secondary },
    { label: 'WAIT', value: report.outcomeDistribution.wait, color: colors.primary },
    { label: 'Pending', value: report.outcomeDistribution.pending, color: colors.muted },
  ];

  const outcomeColWidth = contentWidth / 3;
  outcomes.forEach((outcome, i) => {
    const x = margin + (i % 3) * outcomeColWidth;
    const y = yPos + Math.floor(i / 3) * 14;
    
    // Small color dot
    doc.setFillColor(...outcome.color);
    doc.circle(x + 3, y - 2, 2, 'F');
    
    addText(outcome.label, x + 8, y, { fontSize: 9, color: colors.text });
    addText(outcome.value.toString(), x + 50, y, { fontSize: 10, fontStyle: 'bold', color: outcome.color });
  });

  yPos += 35;

  // Market breakdown
  addText('By Market Type:', margin, yPos, { fontSize: 9, fontStyle: 'bold', color: colors.text });
  yPos += 8;
  Object.entries(report.categoryBreakdown.byMarketType).forEach(([market, count], i) => {
    addText(`${market}: ${count}`, margin + (i * 45), yPos, { fontSize: 9, color: colors.muted });
  });

  yPos += 20;

  // ==================== PAGE 3: DECISION LOG ====================

  doc.addPage();
  yPos = margin;

  // Header
  addText('DECISION LOG', margin, yPos, { fontSize: 12, fontStyle: 'bold', color: colors.dark });
  addText('Appendix - Last 50 entries', pageWidth - margin, yPos, { fontSize: 9, color: colors.muted, align: 'right' });
  yPos += 12;

  // Table
  const colWidths = [25, 30, 35, 22, 30, 28];
  const headers = ['Date', 'Asset', 'Decision', 'Dir', 'Outcome', 'R'];
  
  // Table header
  drawCard(margin, yPos, contentWidth, 10, colors.darkCard);
  yPos += 7;
  let xPos = margin + 3;
  headers.forEach((h, i) => {
    addText(h, xPos, yPos, { fontSize: 8, fontStyle: 'bold', color: colors.white });
    xPos += colWidths[i];
  });
  yPos += 7;

  // Table rows
  report.decisionLog.slice(0, 50).forEach((entry, rowIndex) => {
    if (yPos > pageHeight - 20) {
      doc.addPage();
      yPos = margin;
      // Re-add header
      drawCard(margin, yPos, contentWidth, 10, colors.darkCard);
      yPos += 7;
      xPos = margin + 3;
      headers.forEach((h, i) => {
        addText(h, xPos, yPos, { fontSize: 8, fontStyle: 'bold', color: colors.white });
        xPos += colWidths[i];
      });
      yPos += 7;
    }

    if (rowIndex % 2 === 0) {
      drawCard(margin, yPos - 4, contentWidth, 9, colors.light);
    }

    xPos = margin + 3;
    const decisionColor = (entry.decision || '').includes('NO_TRADE') ? colors.secondary : 
                         (entry.decision || '').includes('WAIT') ? colors.primary : colors.text;
    const outcomeColor = (entry.outcome || '') === 'WIN' ? colors.success :
                        (entry.outcome || '') === 'LOSS' ? colors.danger : colors.text;
    
    const row = [
      { text: entry.date || '-', color: colors.text },
      { text: (entry.asset || '-').slice(0, 8), color: colors.text },
      { text: (entry.decision || '-').slice(0, 10), color: decisionColor },
      { text: (entry.direction || '-').slice(0, 5), color: colors.text },
      { text: (entry.outcome || '-').slice(0, 8), color: outcomeColor },
      { text: entry.rMultiple !== null && entry.rMultiple !== undefined ? entry.rMultiple.toFixed(1) : '-', color: colors.text },
    ];
    
    row.forEach((cell, i) => {
      addText(cell.text, xPos, yPos, { fontSize: 7, color: cell.color });
      xPos += colWidths[i];
    });
    yPos += 9;
  });

  // Footer on each page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawLine(pageHeight - 12, colors.light);
    addText('Snap Chart AI - Institutional Trading Intelligence', margin, pageHeight - 6, { fontSize: 7, color: colors.muted });
    addText(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 6, { fontSize: 7, color: colors.muted, align: 'right' });
  }

  return doc;
}

// =====================================================
// PUBLIC API
// =====================================================

export async function generateAuditReport(
  preset: DateRangePreset,
  customRange?: { from: Date; to: Date }
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('[AuditExport] Starting report generation...');
    
    const dateRange = preset === 'custom' && customRange 
      ? { ...customRange, label: 'Custom Range' }
      : getDateRangeFromPreset(preset);

    const params: AuditReportParams = {
      dateRange,
    };

    console.log('[AuditExport] Fetching data...');
    const reportData = await fetchAuditData(params);
    
    if (!reportData) {
      return { success: false, error: 'Failed to fetch report data' };
    }

    console.log('[AuditExport] Generating PDF...');
    const pdf = generatePDF(reportData);

    // Download the PDF
    const fileName = `SnapChartAI_Audit_${dateRange.label.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    console.log('[AuditExport] Report generated successfully');
    return { success: true };
  } catch (error) {
    console.error('[AuditExport] Error generating report:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}
